import { useMemo, useRef, useState, type ReactNode } from "react";

import type { AssetDataRecord, AssetDataSignal, AssetDataVal } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { dateTime } from "@/lib/format";
import { pretty } from "@/modules/asset_data/labels";

/** Format a decoded value as TEXT: integers as-is, floats to 2 dp, strings
 *  verbatim, null as a dash. Exported and used outside the table (the
 *  dashboard tiles), so it stays a plain string. */
export function fmt(v: AssetDataVal): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return String(v);
}

/** The same value as a table CELL, where a missing reading must not look like
 *  a reading.
 *
 *  `fmt` said "null as a dim dash" in its doc comment and returned a bare
 *  string, so the dash arrived in the cell's own colour — and since
 *  `.tbl td` is `--t-mute`, EVERY value was as quiet as a missing one. The
 *  result was a wall of uniform grey where the eye has nothing to catch. The
 *  ink now follows the data: values in `--t-body` (set on `.tbl.zebra`),
 *  nulls alone in `--t-dim` via `.nil`. */
function cell(v: AssetDataVal): ReactNode {
  if (v === null || v === undefined || v === "") return <span className="nil">—</span>;
  return fmt(v);
}

/** `vehicle_control_status` decoded, IN THE TABLE.
 *
 *  The raw word is a five-digit integer — 65583 — and nobody can read a
 *  bitfield at a glance, so the column was present and useless. This renders
 *  the one fact that matters per row and keeps the number in the tooltip.
 *
 *  NO BIT ARITHMETIC HERE, deliberately. The layout lives in the firmware's C
 *  and in `shared/lib/vehicle_control.py`; a third copy in the browser is how
 *  a renumbered bit ends up looking right on screen while reporting a
 *  restraint that moved. The HISTORIC rows in this table are raw words with no
 *  decoded block beside them, so the only thing read off the number is
 *  whether it is zero — "nothing commanded, nothing enforced" — and anything
 *  else is shown as the value it is, for the live dashboard above to explain.
 *  A column that silently decoded old rows with today's bit meanings would be
 *  the drift this split exists to prevent. */
function controlCell(v: AssetDataVal): ReactNode {
  if (typeof v !== "number") return cell(v);
  const col = v === 0 ? "var(--t-mute)" : "var(--info)";
  return (
    <span className="chip" title={`vehicle_control_status = ${v} (0x${v.toString(16)})`}
      style={{ color: col, background: v === 0 ? "var(--well)" : "var(--info-soft)",
               fontSize: 10.5, cursor: "help" }}>
      {v === 0 ? "idle" : `0x${v.toString(16)}`}
    </span>
  );
}

/** A catalog signal plus whether its column holds NUMBERS.
 *
 *  Sampled from the rows rather than declared, because the long tail is
 *  discovered at runtime: a catalog entry has a unit, a vendor CAN slot has
 *  nothing at all. */
type Col = AssetDataSignal & {
  numeric: boolean;
  /** decoded but NOT in the signal catalog — the vendor long tail (gps_*,
   *  CAN slots, versions). Drives the group divider, so "All columns" reads
   *  as two blocks instead of one eighty-column run. */
  extra: boolean;
};

function isNumericCol(records: AssetDataRecord[], key: string): boolean {
  for (const r of records) {
    const x = r.values[key];
    if (x !== null && x !== undefined && x !== "") return typeof x === "number";
  }
  return false;
}

/** Columns = TS · SEQ · every catalog signal present (labelled + unit), THEN
 *  every other decoded field present (the long tail — gps_*, status_bits, batt_*,
 *  CAN slots, versions…), appended and prettified. This is the "dash-tool" view:
 *  one column per decoded variable, nothing hidden. */
export function useColumns(records: AssetDataRecord[], signals: AssetDataSignal[]): Col[] {
  const computed = useMemo<Col[]>(() => {
    const present = new Set<string>();
    for (const r of records) for (const k of Object.keys(r.values)) present.add(k);
    const catalog = signals.filter((s) => present.has(s.key));
    const known = new Set(signals.map((s) => s.key));
    const extras: AssetDataSignal[] = [...present]
      .filter((k) => !known.has(k))
      .sort()
      .map((k) => ({ key: k, label: pretty(k), unit: "" }));
    // ALIGNMENT IS DECIDED HERE, not in the consumer, and that placement is
    // the whole point. `records` gets a new array identity on every append, so
    // sampling it inside the consumer's `cols` memo would rebuild the column
    // array every tick -- a fresh identity straight into DataTable, defeating
    // its row memoization. That is the render storm this file already carries
    // a fix for; adding a second source of it would be the same bug twice.
    //
    // Sampled here, numeric-ness rides the column SET, which changes only
    // when a key appears or disappears.
    return [
      ...catalog.map((c) => ({ ...c, extra: false })),
      ...extras.map((c) => ({ ...c, extra: true })),
    ].map((c) => ({ ...c, numeric: isNumericCol(records, c.key) }));
  }, [records, signals]);

  // IDENTITY-STABLE columns: `records` gets a new array identity on every
  // append, but the column SET almost never changes — keep the previous array
  // whenever the keys match so DataTable's memoized rows keep skipping.
  //
  // `numeric` is compared too: a column whose first batch was all nulls starts
  // left-aligned, and must be allowed to correct ONCE when a real value
  // arrives. That is one re-render on first sight of data, not one per tick.
  const stable = useRef(computed);
  if (
    stable.current.length !== computed.length ||
    computed.some((c, i) => c.key !== stable.current[i]?.key
                         || c.numeric !== stable.current[i]?.numeric
                         || c.extra !== stable.current[i]?.extra)
  ) {
    stable.current = computed;
  }
  return stable.current;
}

/** The decoded telemetry table — a DataTable consumer. `newestFirst` flips row
 *  order for the live window (freshest on top); History leaves it
 *  oldest→newest for replay. Default column set = catalog key signals; the
 *  vendor long tail mounts behind the "All columns" toggle (cells are the
 *  mount budget — UI law 7). */
export function RecordTable({ records, signals, newestFirst = false, maxHeight = 420, pageSize = 100 }: {
  records: AssetDataRecord[]; signals: AssetDataSignal[]; newestFirst?: boolean; maxHeight?: number;
  /** cells are the mount budget: 100 rows × ~45 "All columns" ≈ 4.5k cells
   *  mounted — a 5000-row History window unpaged was ~225k (measured freeze) */
  pageSize?: number;
}) {
  const allCols = useColumns(records, signals);
  const [showAll, setShowAll] = useState(false);
  // Time sort: records arrive oldest→newest, so "sorting" is just a reverse —
  // the header toggles direction (▼ newest first / ▲ oldest first)
  const [desc, setDesc] = useState(newestFirst);
  const sigCols = useMemo(() => {
    if (showAll) return allCols;
    const known = new Set(signals.map((s) => s.key));
    const key = allCols.filter((c) => known.has(c.key));
    return key.length ? key : allCols;      // no catalog match → show everything
  }, [allCols, signals, showAll]);

  const cols = useMemo<DataCol<AssetDataRecord>[]>(() => [
    {
      key: "__ts",
      label: (
        <span style={{ cursor: "pointer", userSelect: "none" }} title="Sort by time"
          onClick={() => setDesc((d) => !d)}>
          Time {desc ? "▼" : "▲"}
        </span>
      ),
      // THE ANCHOR COLUMN CARRIES THE INK. It is the one cell that is still on
      // screen after a horizontal scroll past forty columns, so it is the
      // thing the eye returns to -- and it was styled exactly like every
      // number beside it.
      width: 168, mono: true,
      render: (r) => <span style={{ color: "var(--t-bright)", fontWeight: 600 }}>{dateTime(r.ts)}</span>,
      title: (r) => new Date(r.ts).toLocaleString(),
    },
    {
      key: "__seq", label: "Seq", width: 58, mono: true, align: "right",
      render: (r) => r.seq,
    },
    ...sigCols.map((c, i): DataCol<AssetDataRecord> => ({
      key: c.key,
      label: <>{c.label}{c.unit && <span style={{ color: "var(--t-dim)", fontWeight: 400 }}> {c.unit}</span>}</>,
      width: c.key === "vehicle_control_status" ? 112 : 104,
      mono: true,
      // TWO DIVIDERS, both earning their place. One after Time/Seq separates
      // the ANCHOR block from the data, so a 45-column dump has something to
      // count from; one at the first vendor field splits "All columns" into
      // the catalog signals an operator knows and the long tail they are
      // spelunking, instead of one undifferentiated run of digits.
      groupStart: i === 0 || (c.extra && !sigCols[i - 1]?.extra),
      align: c.numeric ? "right" : "left",
      render: c.key === "vehicle_control_status"
        ? (r) => controlCell(r.values[c.key] ?? null)
        : (r) => cell(r.values[c.key] ?? null),
      title: (r) => {
        const s = fmt(r.values[c.key] ?? null);
        return s.length > 12 ? s : undefined;
      },
    })),
  ], [sigCols, desc]);

  const rows = useMemo(
    () => (desc ? [...records].reverse() : records),
    [records, desc],
  );
  const hiddenCount = allCols.length - sigCols.length;

  return (
    <DataTable
      rows={rows}
      cols={cols}
      rowKey={(r) => r.cursor}
      zebra
      stickyFirst
      maxHeight={maxHeight}
      fullscreenable
      pageSize={pageSize}
      empty={
        <div className="panel panel-pad cap">
          No decoded records yet for this device.
        </div>
      }
      toolbar={
        <button className="btn ghost" onClick={() => setShowAll((s) => !s)}
          title="Key signals = the catalog columns; All adds every decoded vendor field">
          {showAll ? `Key signals only` : `All columns${hiddenCount > 0 ? ` (+${hiddenCount})` : ""}`}
        </button>
      }
    />
  );
}
